import { Icon } from '@telavita-core/react-design-kit'
import React, { FC, useCallback, useEffect, useState } from 'react'
import {
  CustomSearchField,
  CustomSearchFieldProps,
} from '../CustomSearchField'

import * as S from './styles'
import { Option } from '../../types/option'
import { Pagination } from 'services/medicalRecords/pagination.types'

interface OptionsResponse {
  pagination: Pagination
}

interface SearchMultiSelectProps
  extends Omit<
    CustomSearchFieldProps,
    'value' | 'onSelectOption' | 'next' | 'onChange'
  > {
  selectedOptions: Option[]
  onChange: (value: Option[]) => void
  errorMessage?: string
  loadOptions?: (
    query: string,
    page: number,
    isSearch?: boolean
  ) => Promise<OptionsResponse>
}

export const SearchMultiSelect: FC<SearchMultiSelectProps> = (props) => {
  const {
    selectedOptions,
    onChange,
    errorMessage,
    label,
    description,
    loadOptions,
    options,
    floatingLabel,
    inputPlaceholder,
    emptyOptionsMessage,
  } = props

  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
  })
  const [search, setSearch] = useState('')

  const [loading, setLoading] = useState(false)

  const hasError = Boolean(errorMessage)

  function handleSelectOption(optionId: number) {
    const selectedOption = options.find((op) => op.id === optionId)

    if (selectedOptions.includes(selectedOption)) return
    onChange([...selectedOptions, selectedOption])
  }

  const loadData = useCallback(
    async (query: string, page: number, isSearch = false) => {
      setLoading(true)
      const result: OptionsResponse = await loadOptions(query, page, isSearch)
      setPagination(result.pagination)
      setLoading(false)
    },
    [loadOptions]
  )

  async function handleSearch(value: string) {
    setSearch(value)
    if (value.length >= 3) {
      await loadData(value, 1, true)
    } else if (value === '') {
      handleClearSearchField()
    }
  }

  async function handleClearSearchField() {
    setSearch('')
    loadData('', 1, true)
  }

  useEffect(() => {
    loadData('', 1)
  }, [loadData])

  return (
    <S.SearchMultiSelectContainer>
      <CustomSearchField
        label={label}
        description={description}
        options={options}
        loading={loading}
        floatingLabel={floatingLabel}
        inputPlaceholder={inputPlaceholder}
        emptyOptionsMessage={emptyOptionsMessage}
        onSelectOption={handleSelectOption}
        clearSearchField={handleClearSearchField}
        optionIcon={<Icon name='Plus' height={24} width={24} fill='#898989' />}
        next={() => loadData(search, pagination.page + 1)}
        hasMore={loading || pagination.total > options.length}
        value={search}
        onChange={handleSearch}
        selectedOptions={selectedOptions}
        selectedOptionIcon={
          <Icon name='Check' height={24} width={24} fill='#c1c1c1' />
        }
        hasError={hasError}
        messageError={errorMessage}
        closeOnSelect={false}
      />
    </S.SearchMultiSelectContainer>
  )
}
